import type { AdminIdentity, PageResult, UserListItem } from '../types';

const API_BASE = (import.meta.env.VITE_ADMIN_API_BASE_URL || '').replace(/\/$/, '');
const API_PREFIX = `${API_BASE}/api/admin/v1`;
const ACCESS_TOKEN_KEY = 'pengpeng-admin-access-token';
const CSRF_TOKEN_KEY = 'pengpeng-admin-csrf-token';
const REQUEST_TIMEOUT = 20_000;
let refreshPromise: Promise<boolean> | null = null;

export class AdminApiError extends Error {
  constructor(public status: number, public code: string, message: string, public requestId?: string) {
    super(message);
    this.name = 'AdminApiError';
  }
}

export function getAccessToken() {
  return sessionStorage.getItem(ACCESS_TOKEN_KEY) || '';
}

export function setSessionTokens(accessToken: string, csrfToken?: string) {
  if (accessToken) sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  if (csrfToken) localStorage.setItem(CSRF_TOKEN_KEY, csrfToken);
}

export function clearSessionTokens() {
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(CSRF_TOKEN_KEY);
}

/**
 * 个人照片由管理 API 代取，避免后台域名不在 OSS 防盗链白名单时图片无法显示。
 * 返回 Blob 而不是公开 URL，浏览器不会再直接向 OSS 暴露管理端 Referer。
 */
export async function fetchAdminPersonalPhoto(url: string): Promise<Blob> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
  try {
    const response = await fetch(`${API_PREFIX}/media/personal-photos?${new URLSearchParams({ url })}`, {
      credentials: 'include',
      headers: getAccessToken() ? { Authorization: `Bearer ${getAccessToken()}` } : {},
      signal: controller.signal,
    });
    if (response.status === 401 && await refreshAccessToken()) return fetchAdminPersonalPhoto(url);
    if (!response.ok) throw new Error(`图片读取失败（${response.status}）`);
    return response.blob();
  } finally {
    window.clearTimeout(timer);
  }
}

async function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
      try {
        const response = await fetch(`${API_PREFIX}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'X-Admin-CSRF': localStorage.getItem(CSRF_TOKEN_KEY) || '' },
          signal: controller.signal,
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || payload.ok === false) {
          clearSessionTokens();
          return false;
        }
        setSessionTokens(payload.data.accessToken);
        return true;
      } catch {
        // 刷新请求超时或断网时不能让整个后台初始化永久等待。
        clearSessionTokens();
        return false;
      } finally {
        window.clearTimeout(timer);
      }
    })().finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
  retryAuth?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { skipAuth = false, retryAuth = true, ...init } = options;
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
  const headers = new Headers(init.headers);
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  if (init.body != null && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  const token = skipAuth ? '' : getAccessToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(String(init.method || 'GET').toUpperCase()) && !path.startsWith('/auth/')) {
    headers.set('Idempotency-Key', globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`);
  }
  let response: Response;
  try {
    response = await fetch(`${API_PREFIX}${path}`, { ...init, headers, credentials: 'include', signal: controller.signal });
  } catch (error) {
    throw new AdminApiError(0, 'ADMIN_NETWORK_ERROR', controller.signal.aborted ? '请求超时，请稍后重试' : '无法连接后台服务');
  } finally {
    window.clearTimeout(timer);
  }
  const payload = await response.json().catch(() => ({}));
  if (response.status === 401 && !skipAuth && retryAuth && path !== '/auth/refresh') {
    if (await refreshAccessToken()) {
      // 写请求重试必须复用原幂等键。否则响应在服务端提交后丢失、前端又因
      // access token 刷新重发时，会被误识别为第二次独立操作。
      const retryHeaders = new Headers(options.headers);
      const idempotencyKey = headers.get('Idempotency-Key');
      if (idempotencyKey && !retryHeaders.has('Idempotency-Key')) retryHeaders.set('Idempotency-Key', idempotencyKey);
      return request<T>(path, { ...options, headers: retryHeaders, retryAuth: false });
    }
    window.dispatchEvent(new CustomEvent('pengpeng-admin:auth-required'));
  }
  if (!response.ok || payload.ok === false) {
    throw new AdminApiError(response.status, payload.code || 'ADMIN_REQUEST_FAILED', payload.message || `请求失败（${response.status}）`, payload.requestId);
  }
  return payload.data as T;
}

function queryString(params: Record<string, unknown>) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== '' && value != null) search.set(key, String(value));
  });
  const text = search.toString();
  return text ? `?${text}` : '';
}

async function downloadFile(path: string, fallbackFileName: string) {
  const requestDownload = async () => fetch(`${API_PREFIX}${path}`, {
    credentials: 'include',
    headers: { Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', ...(getAccessToken() ? { Authorization: `Bearer ${getAccessToken()}` } : {}) },
  });
  let response: Response;
  try { response = await requestDownload(); } catch { throw new AdminApiError(0, 'ADMIN_NETWORK_ERROR', '无法连接后台服务'); }
  if (response.status === 401 && await refreshAccessToken()) response = await requestDownload();
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new AdminApiError(response.status, payload.code || 'ADMIN_REQUEST_FAILED', payload.message || `导出失败（${response.status}）`, payload.requestId);
  }
  const contentDisposition = response.headers.get('content-disposition') || '';
  const filename = contentDisposition.match(/filename="?([^";]+)"?/i)?.[1] || fallbackFileName;
  const objectUrl = URL.createObjectURL(await response.blob());
  const anchor = document.createElement('a'); anchor.href = objectUrl; anchor.download = filename; document.body.appendChild(anchor); anchor.click(); anchor.remove(); window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1_000);
}

export const adminApi = {
  login: (body: { username: string; password: string; remember: boolean }) => request<{ accessToken: string; csrfToken: string; admin: AdminIdentity }>('/auth/login', { method: 'POST', body: JSON.stringify(body), skipAuth: true }),
  logout: () => request('/auth/logout', { method: 'POST', skipAuth: true }).finally(clearSessionTokens),
  session: () => request<{ admin: AdminIdentity }>('/auth/session'),
  changePassword: (body: { currentPassword: string; newPassword: string }) => request('/auth/change-password', { method: 'POST', body: JSON.stringify(body) }),
  dashboard: () => request<any>('/dashboard/summary'),
  dashboardTrends: (params: { startDate: string; endDate: string }) => request<any>(`/dashboard/trends${queryString(params)}`),
  dashboardAnalyticsFunnel: (params: Record<string, unknown>) => request<any>(`/dashboard/analytics-funnel${queryString(params)}`),
  dashboardTodos: () => request<{ pendingReports: number; pendingAppeals: number; pendingFeedback: number; pendingOutbox: number; total: number }>('/dashboard/todos'),
  systemHealth: () => request<any>('/system/health'),
  outboxEvents: (status = 'active') => request<any[]>(`/system/outbox-events${queryString({ status })}`),
  retryOutboxEvent: (id: string) => request(`/system/outbox-events/${encodeURIComponent(id)}/retry`, { method: 'POST' }),
  systemCapacity: (days = 14) => request<any>(`/system/capacity${queryString({ days })}`),
  users: (params: Record<string, unknown>) => request<PageResult<UserListItem>>(`/users${queryString(params)}`),
  userFilterOptions: () => request<any>('/users/filter-options'),
  userDetail: (id: string) => request<any>(`/users/${encodeURIComponent(id)}`),
  userEligibility: (id: string) => request<any>(`/users/${encodeURIComponent(id)}/match-eligibility`),
  userPrivateContact: (id: string, reason: string) => request<{ wechatId: string }>(`/users/${encodeURIComponent(id)}/private-contact`, { method: 'POST', body: JSON.stringify({ reason }) }),
  banUser: (id: string, body: any) => request(`/users/${encodeURIComponent(id)}/ban`, { method: 'POST', body: JSON.stringify(body) }),
  unbanUser: (id: string, body: any) => request(`/users/${encodeURIComponent(id)}/unban`, { method: 'POST', body: JSON.stringify(body) }),
  unhibernateUser: (id: string, reason: string) => request(`/users/${encodeURIComponent(id)}/unhibernate`, { method: 'POST', body: JSON.stringify({ reason }) }),
  adjustUserValue: (id: string, body: any) => request(`/users/${encodeURIComponent(id)}/value-adjustments`, { method: 'POST', body: JSON.stringify(body) }),
  patchUserProfile: (id: string, body: any) => request(`/users/${encodeURIComponent(id)}/profile`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteUserPersonalPhoto: (id: string, body: { photoUrl: string; reason: string }) => request(`/users/${encodeURIComponent(id)}/personal-photos`, { method: 'DELETE', body: JSON.stringify(body) }),
  matches: (params: Record<string, unknown>) => request<any>(`/matches${queryString(params)}`),
  matchAlgorithm: () => request<any>('/match-algorithm'),
  simulateMatch: (leftUserId: string, rightUserId: string) => request<any>('/match-algorithm/simulate', { method: 'POST', body: JSON.stringify({ leftUserId, rightUserId }) }),
  cases: (params: Record<string, unknown>) => request<any>(`/cases${queryString(params)}`),
  reviewCase: (id: string, body: any) => request(`/cases/${encodeURIComponent(id)}/review`, { method: 'POST', body: JSON.stringify(body) }),
  posts: (params: Record<string, unknown>) => request<any>(`/planet/posts${queryString(params)}`),
  postAction: (id: string, body: any) => request(`/planet/posts/${encodeURIComponent(id)}/action`, { method: 'POST', body: JSON.stringify(body) }),
  comments: (params: Record<string, unknown>) => request<any>(`/planet/comments${queryString(params)}`),
  commentAction: (id: string, body: any) => request(`/planet/comments/${encodeURIComponent(id)}/action`, { method: 'POST', body: JSON.stringify(body) }),
  articles: (params: Record<string, unknown>) => request<any>(`/inspirations${queryString(params)}`),
  saveArticle: (id: string | null, body: any) => request(id ? `/inspirations/${id}` : '/inspirations', { method: id ? 'PUT' : 'POST', body: JSON.stringify(body) }),
  articleAction: (id: string, body: any) => request(`/inspirations/${id}/action`, { method: 'POST', body: JSON.stringify(body) }),
  polls: () => request<any[]>('/polls/questions'),
  savePoll: (id: string | null, body: any) => request(id ? `/polls/questions/${id}` : '/polls/questions', { method: id ? 'PUT' : 'POST', body: JSON.stringify(body) }),
  setPollStatus: (id: string, body: any) => request(`/polls/questions/${id}/status`, { method: 'PATCH', body: JSON.stringify(body) }),
  publishPollSchedule: (body: { entries: Array<{ dateKey: string; questionId: string }>; note: string }) =>
    request<{ count: number }>('/polls/schedules/publish', { method: 'POST', body: JSON.stringify(body) }),
  pollSchedules: (params: { startDate: string; endDate: string }) => request<{ items: any[] }>(`/polls/schedules${queryString(params)}`),
  savePollSchedules: (body: { entries: Array<{ dateKey: string; questionId: string | null }>; note: string }) =>
    request<{ count: number }>('/polls/schedules', { method: 'PUT', body: JSON.stringify(body) }),
  moderationQueue: (params: Record<string, unknown>) => request<any>(`/moderation/queue${queryString(params)}`),
  reviewModeration: (id: string, body: any) => request(`/moderation/queue/${id}/review`, { method: 'POST', body: JSON.stringify(body) }),
  sensitiveWords: (params: Record<string, unknown>) => request<any>(`/sensitive-words${queryString(params)}`),
  saveSensitiveWord: (id: string | null, body: any) => request(id ? `/sensitive-words/${id}` : '/sensitive-words', { method: id ? 'PUT' : 'POST', body: JSON.stringify(body) }),
  publishSensitiveWords: (note: string) => request('/sensitive-words/publish', { method: 'POST', body: JSON.stringify({ note }) }),
  walletLogs: (params: Record<string, unknown>) => request<any>(`/wallet/logs${queryString(params)}`),
  financeOverview: (params: { startDate: string; endDate: string }) => request<any>(`/finance/overview${queryString(params)}`),
  financeWalletLogs: (params: Record<string, unknown>) => request<any>(`/finance/wallet-logs${queryString(params)}`),
  leaderboardOrders: (params: Record<string, unknown>) => request<any>(`/leaderboard/orders${queryString(params)}`),
  operationConfiguration: () => request<any>('/operations/configuration'),
  saveOperationNotes: (body: any) => request<any>('/operations/notes', { method: 'PUT', body: JSON.stringify(body) }),
  saveOperationNotice: (body: any) => request<any>('/operations/notice', { method: 'PUT', body: JSON.stringify(body) }),
  saveOperationOfficialAccounts: (body: any) => request<any>('/operations/official-accounts', { method: 'PUT', body: JSON.stringify(body) }),
  paymentOrders: (params: Record<string, unknown>) => request<any>(`/payments${queryString(params)}`),
  downloadPaymentOrdersExcel: (params: Record<string, unknown>) => downloadFile(`/payments/export${queryString(params)}`, `pengpeng-payment-orders-${new Date().toISOString().slice(0, 10)}.xlsx`),
  requestPaymentRefund: (body: { outTradeNos: string[]; reason: string }) => request<any>('/payments/refunds', { method: 'POST', body: JSON.stringify(body) }),
  reconcilePaymentRefunds: (body: { outTradeNos: string[] }) => request<any>('/payments/refunds/reconcile', { method: 'POST', body: JSON.stringify(body) }),
  paymentStatus: () => request<any>('/payments/status'),
  configs: () => request<any>('/configs'),
  saveConfigDraft: (key: string, body: any) => request(`/configs/${encodeURIComponent(key)}/draft`, { method: 'PUT', body: JSON.stringify(body) }),
  publishConfig: (key: string, note: string) => request(`/configs/${encodeURIComponent(key)}/publish`, { method: 'POST', body: JSON.stringify({ note }) }),
  rollbackConfig: (key: string, note: string) => request(`/configs/${encodeURIComponent(key)}/rollback`, { method: 'POST', body: JSON.stringify({ note }) }),
  policies: () => request<any[]>('/policies'),
  savePolicy: (body: any) => request('/policies', { method: 'POST', body: JSON.stringify(body) }),
  updatePolicy: (id: string, body: any) => request(`/policies/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(body) }),
  deletePolicy: (id: string) => request(`/policies/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  publishPolicy: (id: string, note: string) => request(`/policies/${id}/publish`, { method: 'POST', body: JSON.stringify({ note }) }),
  adminAccounts: () => request<any[]>('/admin-accounts'),
  roles: () => request<any[]>('/roles'),
  createRole: (body: { name: string; description?: string; moduleKeys: string[] }) => request('/roles', { method: 'POST', body: JSON.stringify(body) }),
  updateRole: (id: string, body: { name: string; description?: string; moduleKeys: string[] }) => request(`/roles/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteRole: (id: string, reason: string) => request(`/roles/${encodeURIComponent(id)}`, { method: 'DELETE', body: JSON.stringify({ reason }) }),
  createAdmin: (body: any) => request('/admin-accounts', { method: 'POST', body: JSON.stringify(body) }),
  setAdminStatus: (id: string, body: any) => request(`/admin-accounts/${id}/status`, { method: 'PATCH', body: JSON.stringify(body) }),
  resetAdminPassword: (id: string, body: any) => request(`/admin-accounts/${id}/reset-password`, { method: 'POST', body: JSON.stringify(body) }),
  setAdminRoles: (id: string, body: any) => request(`/admin-accounts/${id}/roles`, { method: 'PUT', body: JSON.stringify(body) }),
  auditLogs: (params: Record<string, unknown>) => request<any>(`/audit-logs${queryString(params)}`),
  exportJobs: () => request<any[]>('/exports'),
  createExport: (body: { exportType: 'users_summary' | 'case_records' | 'wallet_summary'; filters?: Record<string, unknown>; rowLimit: number }) =>
    request('/exports', { method: 'POST', body: JSON.stringify(body) }),
  exportDownload: (id: string) => request<{ url: string; expiresIn: number }>(`/exports/${encodeURIComponent(id)}/download`, { method: 'POST' }),
};
